import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import api, { formatApiError } from "@/lib/api";
import { Mail, Send, Loader2, CheckCircle2, XCircle, Clock } from "lucide-react";

const AUDIENCE = [
  { id: "all", label: "Everyone" },
  { id: "student", label: "Students only" },
  { id: "teacher", label: "Teachers only" },
  { id: "admin", label: "Admins only" },
];

const statusTone = (s) => {
  if (s === "done") return { tone: "bg-emerald-50 text-emerald-700", Icon: CheckCircle2 };
  if (s === "failed") return { tone: "bg-rose-50 text-rose-700", Icon: XCircle };
  return { tone: "bg-amber-50 text-amber-700", Icon: Clock };
};

export default function AdminBulkEmail() {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("Hi {name},\n\n");
  const [audience, setAudience] = useState("all");
  const [history, setHistory] = useState([]);
  const [banner, setBanner] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/bulk-email/history").then((r) => setHistory(r.data));
  useEffect(() => { load(); const t = setInterval(load, 4000); return () => clearInterval(t); }, []);

  const send = async (e) => {
    e.preventDefault();
    setErr(""); setBanner(""); setBusy(true);
    try {
      const { data } = await api.post("/bulk-email", { subject, body, audience });
      setBanner(`Queued for ${data.recipients_count} recipient${data.recipients_count === 1 ? "" : "s"}.`);
      setSubject(""); setBody("Hi {name},\n\n");
      load();
    } catch (ex) { setErr(formatApiError(ex)); }
    finally { setBusy(false); }
  };

  return (
    <AppShell title="Bulk email" subtitle="Reach students, teachers or the whole platform in one send.">
      <div className="grid lg:grid-cols-3 gap-6">
        <form onSubmit={send} className="qs-card p-6 lg:col-span-2" data-testid="bulk-email-form">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-700 grid place-items-center">
              <Mail className="w-5 h-5" />
            </div>
            <h2 className="font-display text-lg font-bold text-slate-900">Compose broadcast</h2>
          </div>
          <label className="block text-xs uppercase tracking-widest font-bold text-slate-500 mb-1">Audience</label>
          <select
            value={audience}
            onChange={(e) => setAudience(e.target.value)}
            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
            data-testid="bulk-audience-select"
          >
            {AUDIENCE.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
          </select>
          <label className="block mt-3 text-xs uppercase tracking-widest font-bold text-slate-500 mb-1">Subject</label>
          <input
            required
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Your subject line"
            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
            data-testid="bulk-subject-input"
          />
          <label className="block mt-3 text-xs uppercase tracking-widest font-bold text-slate-500 mb-1">Body</label>
          <textarea
            required
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={10}
            placeholder="Use {name} to personalize the greeting…"
            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none font-mono text-sm"
            data-testid="bulk-body-input"
          />
          <p className="text-xs text-slate-500 mt-1">Placeholders: <code>{"{name}"}</code></p>
          {banner && (
            <div className="mt-3 text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-2" data-testid="bulk-banner">
              {banner}
            </div>
          )}
          {err && (
            <div className="mt-3 text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-xl px-4 py-2" data-testid="bulk-error">
              {err}
            </div>
          )}
          <button disabled={busy} className="btn-primary text-sm mt-5" data-testid="bulk-send-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send broadcast
          </button>
        </form>

        <div className="qs-card p-6" data-testid="bulk-history">
          <h2 className="font-display text-lg font-bold text-slate-900 mb-4">Recent sends</h2>
          <div className="space-y-3">
            {history.length === 0 && (
              <p className="text-sm text-slate-500 py-8 text-center" data-testid="bulk-history-empty">No broadcasts yet.</p>
            )}
            {history.map((h) => {
              const { tone, Icon } = statusTone(h.status);
              return (
                <div key={h.id} className="border border-slate-200 rounded-xl p-3" data-testid={`bulk-history-${h.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-semibold text-sm text-slate-900 line-clamp-1">{h.subject}</p>
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${tone}`}>
                      <Icon className="w-3 h-3" /> {h.status}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1 line-clamp-2">{h.body_preview}</p>
                  <div className="flex items-center justify-between mt-2 text-[11px] text-slate-500">
                    <span>{h.audience} · {h.recipients_count} recipients</span>
                    <span>{h.delivered}/{h.recipients_count} delivered</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
